import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

const PAINAD_POINTS_PER_ITEM = 2;

@Component({
  selector: 'app-painad-questions',
  templateUrl: './painad-questions.component.html',
  styleUrl: './painad-questions.component.css',
  standalone: false,
})
export class PainadQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  totalScore = 0;

  private sub?: Subscription;

  constructor(
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });
    this.recalculateTotal();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges();
  }

  getQuestionTitle(title?: string): string {
    return title?.split('||')[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split('||')[1]?.trim() ?? '';
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && /total/i.test(element.title ?? '');
  }

  get scorableElements(): IQuestionnaireQuestion[] {
    return (this.elements ?? []).filter((el) => el.type === 'radiogroup');
  }

  get maxScore(): number {
    return this.scorableElements.length * PAINAD_POINTS_PER_ITEM;
  }

  private recalculateTotal(): void {
    let total = 0;

    for (const el of this.scorableElements) {
      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
        continue;
      }

      const choiceIndex = (el.choices ?? []).findIndex(
        (c) => c.value === selectedValue,
      );

      if (choiceIndex >= 0) {
        total += choiceIndex;
      }
    }

    this.totalScore = total;

    const totalControlName = (this.elements ?? []).find((el) =>
      this.isTotalScoreElement(el),
    )?.name;
    const totalCtrl = totalControlName
      ? this.formGroup.get(totalControlName)
      : null;

    if (totalCtrl) {
      totalCtrl.setValue(String(this.totalScore), { emitEvent: false });
      totalCtrl.disable({ emitEvent: false });
    }

    this.cdr.detectChanges();
  }
}
